import { Component, EventEmitter, Output, HostListener, OnInit, OnDestroy, inject, computed } from '@angular/core';
import { MatListModule } from '@angular/material/list';
import { CommonModule } from '@angular/common';
import { MatExpansionModule } from '@angular/material/expansion';
import { Helpers } from '../../utilities/helpers';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { navigationData } from '../../utilities/navigationData';
import { CurrentUserState } from '../../state/current-user.state';
import { NavItem } from '../../models/navItem';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, MatListModule, MatExpansionModule, MatIconModule, RouterModule],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit, OnDestroy {
  @Output() toggleSidebar = new EventEmitter<void>();
  isOpen = true;
  isMobile = false;
  expandedPanels = new Set<number>();
  private routerSubscription: Subscription | null = null;
  private currentUserState = inject(CurrentUserState);

  // Filter sidebar items based on user permissions
  items = computed(() => {
    const allItems = navigationData;

    return allItems
      .map(section => ({
        ...section,
        children: section.children?.filter(child =>
          child.route && (child.developerOnly
            // Developer-only items are gated on the Keycloak realm role, not DB section access.
            ? this.currentUserState.isDeveloper()
            : this.currentUserState.hasAccess(section.displayName, child.displayName))
        )
      }))
      .filter(section => section.children && section.children.length > 0);
  });

  @HostListener('window:resize')
  onResize() {
    this.checkScreenSize();
  }

  constructor(private router: Router) {
    this.checkScreenSize();
  }

  ngOnInit() {
    // Check initial route
    this.checkCurrentRoute();

    // Subscribe to route changes
    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.checkCurrentRoute();
    });
  }

  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  private checkCurrentRoute() {
    const currentUrl = this.router.url;

    // Collapse all panels first
    this.expandedPanels.clear();

    this.items().forEach((item, index) => {
      const hasActiveChild = item.children?.some((child: NavItem) => {
        return this.isItemActive(child, currentUrl);
      });

      if (hasActiveChild) {
        this.expandedPanels.add(index);
      }
    });
  }

  onPanelOpened(index: number) {
    // Keep only one panel expanded at a time
    this.expandedPanels.clear();
    this.expandedPanels.add(index);
  }

  onPanelClosed(index: number) {
    this.expandedPanels.delete(index);
  }

  private checkScreenSize() {
    this.isMobile = window.innerWidth < 768;
    if (this.isMobile) {
      this.isOpen = false;
    }
  }

  onToggle() {
    this.isOpen = !this.isOpen;
    this.toggleSidebar.emit();
  }

  hasActiveChild(item: any): boolean {
    const currentUrl = this.router.url;
    return item.children?.some((child: any) => {
      return this.isItemActive(child, currentUrl);
    }) ?? false;
  }

  isItemActive(item: any, currentUrl?: string): boolean {
    if (!item.route) return false;

    const url = currentUrl || this.router.url;

    // Normalize both the current URL and the route for comparison
    const normalizedCurrentUrl = url.startsWith('/') ? url : `/${url}`;
    const normalizedItemRoute = item.route.startsWith('/') ? item.route : `/${item.route}`;

    // Check if the current URL starts with the normalized route
    const isActive = normalizedCurrentUrl.startsWith(normalizedItemRoute);

    // If the item has activeOn routes, check those as well
    if (item.activeOn && Array.isArray(item.activeOn)) {
      const isActiveOnRoute = item.activeOn.some((activeRoute: string) => {
        const normalizedActiveRoute = activeRoute.startsWith('/') ? activeRoute : `/${activeRoute}`;
        return normalizedCurrentUrl.startsWith(normalizedActiveRoute);
      });

      return isActive || isActiveOnRoute;
    }

    return isActive;
  }
}
